class CartDrawerController {
  constructor() {
    this.drawer = document.querySelector('[data-cart-drawer]');
    if (!this.drawer) return;

    this.panel = this.drawer.querySelector('.cart-drawer__panel');
    this.body = this.drawer.querySelector('[data-cart-drawer-body]');
    this.footer = this.drawer.querySelector('[data-cart-drawer-footer]');
    this.liveRegion = this.drawer.querySelector('[data-cart-drawer-live-region]');
    this.countNodes = [...document.querySelectorAll('[data-cart-count]')];
    this.toggleNodes = [...document.querySelectorAll('[data-cart-toggle]')];
    this.drawerCount = this.drawer.querySelector('[data-cart-drawer-count]');
    this.subtotalNode = this.drawer.querySelector('[data-cart-drawer-subtotal]');
    this.compareSubtotalNode = this.drawer.querySelector('[data-cart-drawer-compare-subtotal]');
    this.currency = this.drawer.dataset.cartCurrency || 'USD';
    this.showCompareAt = this.drawer.dataset.cartDrawerShowCompareAt === 'true';
    this.variantCompareAtPrices = this.parseVariantCompareAtPrices();
    this.productCache = new Map();
    this.currentCart = null;
    this.activeAddButton = null;
    this.activeAddLabel = null;
    this.activeAddLabelText = '';
    this.lastActiveElement = null;
    this.pendingItemKeys = new Set();
    this.isRefreshing = false;
    this.closeLabel = this.drawer.dataset.cartDrawerCloseLabel || '';
    this.emptyTitle = this.drawer.dataset.cartDrawerEmptyTitle || '';
    this.emptyCopy = this.drawer.dataset.cartDrawerEmptyCopy || '';
    this.continueUrl = this.drawer.dataset.cartDrawerContinueUrl || '/collections/all';
    this.continueLabel = this.drawer.dataset.cartDrawerContinueLabel || '';
    this.decreaseQuantityLabel = this.drawer.dataset.cartDrawerDecreaseLabel || '';
    this.increaseQuantityLabel = this.drawer.dataset.cartDrawerIncreaseLabel || '';
    this.quantityLabel = this.drawer.dataset.cartDrawerQuantityLabel || '';
    this.removeLabel = this.drawer.dataset.cartDrawerRemoveLabel || '';
    this.subtotalLabel = this.drawer.dataset.cartDrawerSubtotalLabel || '';
    this.secureCheckoutLabel = this.drawer.dataset.cartDrawerSecureCheckoutLabel || '';
    this.liveRegionTemplate = this.drawer.dataset.cartDrawerLiveTemplate || '';
    this.addingLabel = this.drawer.dataset.cartDrawerAddingLabel || '';
    this.loadingLabel = this.drawer.dataset.cartDrawerLoadingLabel || 'Loading cart';
    const paymentIconsTemplate = this.drawer.querySelector('[data-cart-payment-icons-template]');
    this.paymentIconsMarkup = paymentIconsTemplate ? paymentIconsTemplate.innerHTML : '';
    this.reservationTimerNode = this.drawer.querySelector('[data-reservation-timer]');
    this.startReservationTimer();

    this.bindUI();
  }

  startReservationTimer() {
    if (!this.reservationTimerNode) return;
    const storageKey = 'll-cart-reservation-end';
    const durationMs = 10 * 60 * 1000;
    const now = Date.now();
    let endAt = Number(window.sessionStorage.getItem(storageKey) || 0);
    if (!endAt || endAt <= now) {
      endAt = now + durationMs;
      window.sessionStorage.setItem(storageKey, String(endAt));
    }
    const tick = () => {
      let remaining = Math.max(0, endAt - Date.now());
      if (remaining <= 0) {
        endAt = Date.now() + durationMs;
        window.sessionStorage.setItem(storageKey, String(endAt));
        remaining = durationMs;
      }
      const totalSeconds = Math.floor(remaining / 1000);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      this.reservationTimerNode.textContent = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    };
    tick();
    window.setInterval(tick, 1000);
  }

  bindUI() {
    this.toggleNodes.forEach((trigger) => {
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.addEventListener('click', async (event) => {
        event.preventDefault();
        this.lastActiveElement = trigger;
        await this.refresh();
        this.open();
      });
    });

    this.drawer.querySelectorAll('[data-cart-drawer-close]').forEach((button) => {
      button.addEventListener('click', () => this.close());
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.drawer.classList.contains('is-open')) {
        this.close();
      } else if (event.key === 'Tab' && this.drawer.classList.contains('is-open')) {
        this.trapFocus(event);
      }
    });

    document.addEventListener('submit', (event) => this.handleAddToCart(event));
    this.drawer.addEventListener('click', (event) => this.handleDrawerClick(event));
    this.drawer.addEventListener('change', (event) => this.handleDrawerChange(event));
  }

  async handleAddToCart(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (!form.matches('[data-product-form]')) return;

    const submitter = event.submitter;
    if (!submitter || submitter.getAttribute('name') !== 'add') return;

    event.preventDefault();

    const formData = new FormData(form);
    this.activeAddButton = submitter;
    this.activeAddLabel = submitter.querySelector('[data-add-to-cart-label]');
    this.activeAddLabelText = this.activeAddLabel?.textContent || '';
    this.cacheFormVariantCompareAt(form);
    this.hideAddToCartError(form);

    try {
      this.setAddButtonState(true);

      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          Accept: 'application/json'
        },
        body: formData
      });

      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.description || data?.message || '');
      }

      await response.json();
      await this.refresh();
      this.open();
    } catch (error) {
      console.error(error);
      this.showAddToCartError(form, error?.message);
    } finally {
      this.setAddButtonState(false);
    }
  }

  showAddToCartError(form, message) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (!errorElement) {
      window.location.href = form.action || '/cart';
      return;
    }

    errorElement.textContent = message || errorElement.dataset.genericError || '';
    errorElement.hidden = false;
  }

  hideAddToCartError(form) {
    const errorElement = form.querySelector('[data-add-to-cart-error]');
    if (errorElement) {
      errorElement.hidden = true;
      errorElement.textContent = '';
    }
  }

  async handleDrawerClick(event) {
    const quantityTrigger = event.target.closest('[data-cart-quantity-change]');
    if (quantityTrigger) {
      const item = quantityTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      const delta = Number(quantityTrigger.dataset.cartQuantityDelta || 0);
      const quantityValue = item.querySelector('.cart-drawer__quantity-value');
      const currentQuantity = Number(quantityValue?.value ?? quantityValue?.textContent ?? 0);
      const nextQuantity = Math.max(0, currentQuantity + delta);

      await this.changeItem(key, nextQuantity);
      return;
    }

    const removeTrigger = event.target.closest('[data-cart-remove]');
    if (removeTrigger) {
      const item = removeTrigger.closest('[data-cart-item-key]');
      if (!item) return;

      const key = item.dataset.cartItemKey;
      if (!key || this.pendingItemKeys.has(key)) return;

      await this.changeItem(key, 0);
    }
  }

  async handleDrawerChange(event) {
    const input = event.target.closest('[data-cart-quantity-input]');
    if (!input) return;

    const item = input.closest('[data-cart-item-key]');
    if (!item) return;

    const key = item.dataset.cartItemKey;
    if (!key || this.pendingItemKeys.has(key)) return;

    const quantity = Math.max(0, Number(input.value) || 0);
    await this.changeItem(key, quantity);
  }

  async changeItem(key, quantity) {
    if (!key || this.pendingItemKeys.has(key)) return;

    this.pendingItemKeys.add(key);
    this.setItemPendingState(key, true);

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          id: key,
          quantity
        })
      });

      if (!response.ok) {
        throw new Error('Unable to update cart item');
      }

      const cart = await response.json();
      this.render(cart);
    } catch (error) {
      console.error(error);
      this.pendingItemKeys.delete(key);
      await this.refresh();
    } finally {
      this.pendingItemKeys.delete(key);
      this.setItemPendingState(key, false);
    }
  }

  showSkeleton(count = 2) {
    if (!this.body) return;

    const items = Array.from({ length: count }, () => `
      <div class="cart-drawer__skeleton-item" aria-hidden="true">
        <div class="cart-drawer__skeleton-media"></div>
        <div class="cart-drawer__skeleton-lines">
          <div class="cart-drawer__skeleton-line"></div>
          <div class="cart-drawer__skeleton-line"></div>
          <div class="cart-drawer__skeleton-line"></div>
        </div>
      </div>
    `).join('');

    this.body.innerHTML = `<div class="cart-drawer__skeleton" aria-busy="true" aria-label="${this.escapeHtml(this.loadingLabel)}">${items}</div>`;
  }

  async refresh() {
    if (this.isRefreshing) return null;

    this.isRefreshing = true;

    const currentCount = Number(this.drawerCount?.textContent || 0);
    if (currentCount > 0) {
      this.showSkeleton(Math.min(currentCount, 3));
    }

    try {
      const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`, {
        headers: {
          Accept: 'application/json'
        }
      });
      const cart = await response.json();
      this.render(cart);
      return cart;
    } finally {
      this.isRefreshing = false;
    }
  }

  render(cart) {
    this.hydrateCompareAtPrices(cart);
    this.currentCart = cart;
    const itemCount = Number(cart.item_count || 0);
    if (this.drawerCount) this.drawerCount.textContent = itemCount;
    this.countNodes.forEach((node) => { node.textContent = itemCount; node.hidden = itemCount === 0; });
    if (this.liveRegion) this.liveRegion.textContent = `Cart updated. ${itemCount} items. ${this.formatMoney(cart.total_price || 0)} total.`;

    if (!itemCount) {
      this.body.innerHTML = `<div class="cart-drawer__empty"><p class="cart-drawer__empty-title">Your cart is empty</p><p class="cart-drawer__empty-copy">Add something you love and it will appear here.</p><a class="cart-drawer__empty-cta" href="/collections/all">Continue shopping</a></div>`;
      this.footer.hidden = true;
      return;
    }

    this.footer.hidden = false;
    this.body.innerHTML = `${this.renderPromo(itemCount)}${this.renderItems(cart)}`;
    this.footer.innerHTML = this.renderFooter(cart, itemCount);
    void this.hydrateVariantSelectors(cart);
  }

  renderPromo(itemCount) {
    const complete = itemCount >= 4;
    const nextPercent = itemCount < 2 ? 10 : itemCount < 3 ? 15 : 20;
    const message = complete ? 'You unlocked an extra 20% off' : `Add 1 more item to get an extra ${nextPercent}% off`;
    return `<section class="cart-drawer__promo"><p class="cart-drawer__promo-message">${message}</p><div class="cart-drawer__milestones">${[10,15,20].map((percent,index)=>`<div class="cart-drawer__step${itemCount >= index+2 ? ' is-active' : ''}"><span class="cart-drawer__step-track"></span><span class="cart-drawer__step-icon">${this.tagIcon()}</span><span class="cart-drawer__step-label">${percent}% off</span></div>`).join('')}</div></section>`;
  }

  renderMilestone(threshold,itemCount,percent) {
    const active = itemCount >= threshold;
    return `<div class="cart-drawer__milestone-wrap"><span class="cart-drawer__milestone${active ? ' is-active' : ''}" aria-hidden="true"><span></span></span><span class="cart-drawer__milestone-label"><strong>${percent}</strong><span>EXTRA DISCOUNT</span></span></div>`;
  }

  renderItems(cart) {
    return `<ul class="cart-drawer__items" role="list">${(cart.items||[]).map((item)=>{
      const pending=this.pendingItemKeys.has(item.key);
      const oldLine=this.getOldLinePrice(item);
      const finalLine=Number(item.final_line_price||0);
      const saving=Math.max(0,oldLine-finalLine);
      const image=item.image?`<a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-media"><img src="${this.escapeHtml(item.image)}" alt="${this.escapeHtml(item.product_title)}" width="92" height="92" class="cart-drawer__item-image" loading="lazy"></a>`:'<div class="cart-drawer__item-media"></div>';
      return `<li class="cart-drawer__item${pending?' is-pending':''}" data-cart-item-key="${this.escapeHtml(item.key)}" aria-busy="${pending?'true':'false'}">${image}<div class="cart-drawer__item-content"><div class="cart-drawer__item-title-row"><a href="${this.escapeHtml(item.url)}" class="cart-drawer__item-title">${this.escapeHtml(item.product_title)}</a><button type="button" class="cart-drawer__remove" data-cart-remove aria-label="Remove ${this.escapeHtml(item.product_title)}" ${pending?'disabled':''}>${this.trashIcon()}</button></div><p class="cart-drawer__item-variant">${this.escapeHtml(this.getVariantText(item))}</p><div class="cart-drawer__price-line">${saving?`<s class="cart-drawer__item-compare-price">${this.formatMoney(oldLine)}</s>`:''}<span class="cart-drawer__item-price">${this.formatMoney(finalLine)}</span>${saving?`<span class="cart-drawer__item-saving">(Save ${this.formatMoney(saving)})</span>`:''}</div><div class="cart-drawer__quantity"><button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="-1" aria-label="Decrease quantity" ${pending?'disabled':''}>−</button><input type="number" class="cart-drawer__quantity-value" value="${Number(item.quantity||0)}" min="1" aria-label="Quantity" data-cart-quantity-input ${pending?'disabled':''}><button type="button" class="cart-drawer__quantity-button" data-cart-quantity-change data-cart-quantity-delta="1" aria-label="Increase quantity" ${pending?'disabled':''}>+</button></div></div></li>`;
    }).join('')}</ul>`;
  }

  renderOptionSelectors(item) {
    const options = Array.isArray(item.options_with_values)
      ? item.options_with_values.filter((option)=>option?.value && option.value !== 'Default Title').slice(0,2)
      : [];
    if (!options.length) return '';

    const label = options.map((option)=>this.escapeHtml(option.name)).join(', ');
    const controls = options.map((option,index)=>`<label class="cart-drawer__option-box"><select class="cart-drawer__option-select" data-cart-option-select data-cart-option-index="${index}" aria-label="${this.escapeHtml(option.name)}"><option selected>${this.escapeHtml(option.value)}</option></select><svg viewBox="0 0 10 6" aria-hidden="true"><path d="M1 1l4 4 4-4" fill="none" stroke="currentColor" stroke-width="1.25"/></svg></label>`).join('');

    return `<div class="cart-drawer__item-options"><p>${label}:</p><div class="cart-drawer__option-boxes">${controls}</div></div>`;
  }

  getProductPath(item) {
    try {
      const pathname = new URL(item.url, window.location.origin).pathname;
      return pathname.endsWith('/') ? pathname.slice(0, -1) : pathname;
    } catch (error) {
      return '';
    }
  }

  async getProductData(path) {
    if (!path) return null;

    if (!this.productCache.has(path)) {
      const request = fetch(`${path}.js`, { headers: { Accept: 'application/json' } })
        .then((response) => {
          if (!response.ok) throw new Error('Unable to load product variants');
          return response.json();
        })
        .catch((error) => {
          this.productCache.delete(path);
          throw error;
        });

      this.productCache.set(path, request);
    }

    return this.productCache.get(path);
  }

  async hydrateVariantSelectors(cart) {
    await Promise.all((cart.items || []).map(async (item) => {
      const row = this.body?.querySelector(`[data-cart-item-key="${CSS.escape(item.key)}"]`);
      const selects = row ? [...row.querySelectorAll('[data-cart-option-select]')] : [];
      const path = this.getProductPath(item);
      if (!path) return;

      if (row) row.dataset.productPath = path;

      try {
        const product = await this.getProductData(path);
        if (!product?.variants?.length) return;

        const currentVariant = product.variants.find((variant) => Number(variant.id) === Number(item.variant_id));
        if (currentVariant) {
          this.variantCompareAtPrices.set(
            Number(item.variant_id),
            Number(currentVariant.compare_at_price || 0)
          );
        }

        if (!selects.length) return;

        selects.forEach((select) => {
          const index = Number(select.dataset.cartOptionIndex || 0);
          const currentValue =
            item.options_with_values?.[index]?.value ||
            item.variant_options?.[index] ||
            select.value;
          const values = [...new Set(
            product.variants
              .map((variant) => variant.options?.[index])
              .filter(Boolean)
          )];

          select.innerHTML = values.map((value) => {
            const selected = value === currentValue ? ' selected' : '';
            return `<option value="${this.escapeHtml(value)}"${selected}>${this.escapeHtml(value)}</option>`;
          }).join('');
        });
      } catch (error) {
        console.error(error);
      }
    }));

    this.hydrateCompareAtPrices(cart);
    if (this.currentCart === cart) {
      const list = this.body.querySelector('.cart-drawer__items');
      if (list) list.outerHTML = this.renderItems(cart);
    }

    if (this.footer && !this.footer.hidden) {
      this.footer.innerHTML = this.renderFooter(cart, Number(cart.item_count || 0));
    }
  }

  async changeVariantFromSelect(select) {
    const row = select.closest('[data-cart-item-key]');
    if (!row) return;

    const key = row.dataset.cartItemKey;
    if (!key || this.pendingItemKeys.has(key)) return;

    const cartItem = this.currentCart?.items?.find((item) => item.key === key);
    if (!cartItem) {
      await this.refresh();
      return;
    }

    const path = row.dataset.productPath || this.getProductPath(cartItem);
    const product = await this.getProductData(path);
    if (!product?.variants?.length) return;

    const selects = [...row.querySelectorAll('[data-cart-option-select]')]
      .sort((a,b) => Number(a.dataset.cartOptionIndex || 0) - Number(b.dataset.cartOptionIndex || 0));
    const selectedValues = selects.map((node) => node.value);

    const variant = product.variants.find((candidate) =>
      candidate.available &&
      candidate.options?.every((value,index) => value === selectedValues[index])
    );

    if (!variant) {
      await this.refresh();
      return;
    }

    if (Number(variant.id) === Number(cartItem.variant_id)) return;
    await this.replaceCartVariant(cartItem, variant);
  }

  async replaceCartVariant(cartItem, variant) {
    const key = cartItem.key;
    if (!key || this.pendingItemKeys.has(key)) return;

    this.pendingItemKeys.add(key);
    this.setItemPendingState(key, true);

    try {
      const addItem = {
        id: Number(variant.id),
        quantity: Number(cartItem.quantity || 1)
      };

      if (cartItem.properties && Object.keys(cartItem.properties).length) {
        addItem.properties = cartItem.properties;
      }

      const sellingPlanId = cartItem.selling_plan_allocation?.selling_plan?.id;
      if (sellingPlanId) addItem.selling_plan = Number(sellingPlanId);

      const addResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ items: [addItem] })
      });

      if (!addResponse.ok) throw new Error('Unable to add selected variant');

      const removeResponse = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({ id: key, quantity: 0 })
      });

      if (!removeResponse.ok) throw new Error('Unable to remove previous variant');

      this.variantCompareAtPrices.set(Number(variant.id), Number(variant.compare_at_price || 0));

      const cart = await removeResponse.json();
      this.pendingItemKeys.delete(key);
      this.render(cart);
    } catch (error) {
      console.error(error);
      this.pendingItemKeys.delete(key);
      await this.refresh();
    } finally {
      this.pendingItemKeys.delete(key);
      this.setItemPendingState(key, false);
    }
  }

  renderFooter(cart,itemCount) {
    const discount=Number(cart.total_discount||0);
    const applications=[...(cart.cart_level_discount_applications||[]),...(cart.items||[]).flatMap(item=>(item.line_level_discount_allocations||[]).map(entry=>entry.discount_application))].filter(Boolean);
    const percent=applications.find(entry=>entry.value_type==='percentage');
    const original=(cart.items||[]).reduce((sum,item)=>sum+Number(item.original_line_price||0),0);
    const tier=this.getActiveTier(itemCount);
    const matchedTier=tier>0 && Math.abs(discount-Math.round(original*tier/100))<=(cart.items||[]).length;
    const badge=percent?`EXTRA·${Number(percent.value)}%`:(applications[0]?.title||(matchedTier?`EXTRA·${tier}%`:'DISCOUNT'));
    const row=discount>0?`<div class="cart-drawer__discount-row"><div class="cart-drawer__discount-left"><span>Today’s Savings</span><span class="cart-drawer__tier-badge">${this.tagIcon()} ${this.escapeHtml(badge)}</span></div><span class="cart-drawer__discount-amount">- ${this.formatMoney(discount)}</span></div>`:'';
    return `${row}<a href="/checkout" class="cart-drawer__checkout-button"><svg class="cart-drawer__secure-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" aria-hidden="true"><rect x="5" y="10" width="14" height="11" rx="2"/><path d="M8 10V7a4 4 0 0 1 8 0v3"/></svg>CHECKOUT · ${this.formatMoney(cart.total_price||0)}</a><p class="cart-drawer__footer-note">Free U.S. Shipping &amp; 30-Day Money Back Guarantee</p>${this.paymentIconsMarkup}`;
  }

  getActiveTier(itemCount) {
    const count=Number(itemCount||0);
    
    if(count>=4)return 20;
    if(count>=3)return 15;
    if(count>=2)return 10;
    return 0;
  }

  getOldLinePrice(item) {
    const compare=Number(item.compare_at_line_price||0);
    const original=Number(item.original_line_price||0);
    const finalPrice=Number(item.final_line_price||0);
    if(compare>finalPrice)return compare;
    if(original>finalPrice)return original;
    return finalPrice;
  }

  getVariantText(item) {
    if(Array.isArray(item.options_with_values)&&item.options_with_values.length){
      return item.options_with_values.filter((option)=>option?.value&&option.value!=='Default Title').map((option)=>`${option.name}: ${option.value}`).join(', ');
    }
    if(Array.isArray(item.variant_options)&&item.variant_options.length){
      const clean=item.variant_options.filter((value)=>value&&value!=='Default Title');
      if(clean.length===2)return `Color: ${clean[0]}, Size: ${clean[1]}`;
      return clean.join(', ');
    }
    return item.variant_title&&item.variant_title!=='Default Title'?item.variant_title:'';
  }

  tagIcon() {
    return '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M3.5 9.1 9.1 3.5h5.3l2.1 2.1v5.3l-5.6 5.6a1.7 1.7 0 0 1-2.4 0l-5-5a1.7 1.7 0 0 1 0-2.4Z" fill="currentColor"/><circle cx="12.7" cy="7.2" r="1.35" fill="#f8f7f4"/></svg>';
  }

  trashIcon() {
    return '<svg viewBox="0 0 20 20" aria-hidden="true"><path d="M6.4 6.2v9.1m3.6-9.1v9.1m3.6-9.1v9.1M4.5 4.4h11M7.2 4.4l.7-1.7h4.2l.7 1.7m-7.4 0 .8 12.2h7.6l.8-12.2" fill="none" stroke="currentColor" stroke-width="1.35" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  }

  setBackgroundInert(isInert) {
    if (isInert) {
      this.inertedElements = [...document.body.children].filter(
        (el) => el !== this.drawer && el.tagName !== 'SCRIPT' && !el.hasAttribute('inert')
      );
      this.inertedElements.forEach((el) => el.setAttribute('inert', ''));
    } else {
      (this.inertedElements || []).forEach((el) => el.removeAttribute('inert'));
      this.inertedElements = [];
    }
  }

  open() {
    this.drawer.classList.add('is-open');
    this.drawer.setAttribute('aria-hidden', 'false');
    document.body.classList.add('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'true'));
    this.setBackgroundInert(true);

    const focusTarget =
      this.panel.querySelector('#CartDrawerTitle') ||
      this.panel.querySelector('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])');
    focusTarget?.focus();
  }

  close() {
    this.drawer.classList.remove('is-open');
    this.drawer.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('cart-drawer-open');
    this.toggleNodes.forEach((node) => node.setAttribute('aria-expanded', 'false'));
    this.setBackgroundInert(false);
    this.lastActiveElement?.focus();
  }

  setAddButtonState(isLoading) {
    if (!this.activeAddButton) return;

    this.activeAddButton.disabled = isLoading;
    if (this.activeAddLabel) {
      this.activeAddLabel.textContent = isLoading ? this.addingLabel : this.activeAddLabelText;
    }
  }

  setItemPendingState(key, isPending) {
    const item = this.body?.querySelector(`[data-cart-item-key="${CSS.escape(key)}"]`);
    if (!item) return;

    item.classList.toggle('is-pending', isPending);
    item.setAttribute('aria-busy', isPending ? 'true' : 'false');

    item.querySelectorAll('button, select, input').forEach((control) => {
      control.disabled = isPending;
    });
  }

  trapFocus(event) {
    const focusableNodes = [...this.panel.querySelectorAll('button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])')]
      .filter((node) => !node.hasAttribute('disabled'));
    if (!focusableNodes.length) return;

    const firstNode = focusableNodes[0];
    const lastNode = focusableNodes[focusableNodes.length - 1];

    if (event.shiftKey && document.activeElement === firstNode) {
      event.preventDefault();
      lastNode.focus();
    } else if (!event.shiftKey && document.activeElement === lastNode) {
      event.preventDefault();
      firstNode.focus();
    }
  }

  formatMoney(cents) {
    return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
      style: 'currency',
      currency: this.currency
    }).format((cents || 0) / 100);
  }

  escapeHtml(value) {
    return String(value || '')
      .replaceAll('&', '&amp;')
      .replaceAll('<', '&lt;')
      .replaceAll('>', '&gt;')
      .replaceAll('"', '&quot;')
      .replaceAll("'", '&#39;');
  }

  parseJsonNode(selector) {
    const node = this.drawer.querySelector(selector);
    if (!node) return null;

    try {
      return JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.error(error);
      return null;
    }
  }

  parseVariantCompareAtPrices() {
    const value = this.parseJsonNode('[data-cart-drawer-compare-at-prices]');
    const prices = new Map();

    Object.entries(value || {}).forEach(([variantId, compareAtPrice]) => {
      const normalizedId = Number(variantId);
      if (!normalizedId) return;

      prices.set(normalizedId, Number(compareAtPrice || 0));
    });

    return prices;
  }

  cacheFormVariantCompareAt(form) {
    if (!this.showCompareAt) return;

    const variantId = Number(form.querySelector('[name="id"]')?.value || 0);
    if (!variantId || this.variantCompareAtPrices.has(variantId)) return;

    const productJsonNode = form.closest('[data-product-section]')?.querySelector('[data-product-json]');
    if (!productJsonNode) return;

    try {
      const product = JSON.parse(productJsonNode.textContent || '{}');
      const variant = (product.variants || []).find((item) => Number(item.id) === variantId);
      if (!variant) return;

      this.variantCompareAtPrices.set(variantId, Number(variant.compare_at_price || 0));
    } catch (error) {
      console.error(error);
    }
  }

  hydrateCompareAtPrices(cart) {
    if (!cart?.items?.length) return;

    cart.items.forEach((item) => {
      const variantId = Number(item.variant_id || 0);
      const compareAtUnitPrice = Number(this.variantCompareAtPrices.get(variantId) || 0);
      const finalUnitPrice = Number(item.final_price || 0);

      item.compare_at_unit_price = compareAtUnitPrice;
      item.compare_at_line_price =
        compareAtUnitPrice > finalUnitPrice ? compareAtUnitPrice * Number(item.quantity || 0) : 0;
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new CartDrawerController();
});

